import { PromptKeyword } from '@/store/admin'
import {
  Completion,
  CompletionContext,
  CompletionResult,
} from '@codemirror/autocomplete'
import { Cfl } from '@repo/ui/utils'
import {
  Decoration,
  DecorationSet,
  EditorView,
  ViewPlugin,
  ViewUpdate,
} from '@uiw/react-codemirror'
import { type MutableRefObject } from 'react'

export function wrapInHandlebars(value: string) {
  return `{{${value}}}`
}

export const autocomplete =
  (completions: Completion[], isCompletingRef: MutableRefObject<boolean>) =>
  (context: CompletionContext): CompletionResult | null => {
    const typedWord = context.matchBefore(/\w*$/)

    isCompletingRef.current = context.explicit

    if (!context.explicit || !typedWord) return null

    const searchTerm = typedWord.text.trim().toLowerCase()

    const filteredOptions = completions.filter((completion) => {
      const searchLabel = completion.label.toLowerCase()
      const searchDetail = completion.detail?.toLowerCase() || ''

      return (
        searchLabel.includes(searchTerm) || searchDetail.includes(searchTerm)
      )
    })

    const from = typedWord.from
    const options =
      filteredOptions.length > 0
        ? filteredOptions
        : [{ label: 'No results found', apply: () => '' }]

    return {
      from,
      options,
      filter: false,
    }
  }

export const customTagsAutocomplete =
  (completions: Completion[]) => (context: CompletionContext) => {
    const typedWord = context.matchBefore(/<\w*/)
    if (!typedWord || (typedWord.from == typedWord.to && !context.explicit))
      return null
    const searchTerm = typedWord.text.replace(/</g, '')

    const filteredOptions = completions.filter((completion) => {
      const searchLabel = completion.label.toLowerCase()
      const searchDetail = completion.detail?.toLowerCase() || ''

      return (
        searchLabel.includes(searchTerm) || searchDetail.includes(searchTerm)
      )
    })

    const from = typedWord.from
    const to = typedWord.to
    const options =
      filteredOptions.length > 0
        ? filteredOptions
        : [{ label: 'No results found', apply: () => '' }]

    return {
      from: typedWord.from,
      options: options.map((tag) => ({
        label: tag.label,
        type: tag.type,
        apply: (view: EditorView) => {
          const insertText = `${tag.label}></${tag.label}>`
          view.dispatch({ changes: { from: from + 1, to, insert: insertText } })
          view.focus()
          requestAnimationFrame(() => {
            view.dispatch({
              selection: { anchor: from + tag.label.length + 2 },
            })
          })
        },
      })),
      filter: false,
    }
  }

export const highlightValidVariables = (validVariables: string[]) =>
  ViewPlugin.fromClass(
    class {
      decorations: DecorationSet

      constructor(view: EditorView) {
        this.decorations = this.getDecorations(view)
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = this.getDecorations(update.view)
        }
      }

      getDecorations(view: EditorView) {
        const decorations = []
        const regex = /{{(.*?)}}/g

        for (const { from, to } of view.visibleRanges) {
          const text = view.state.doc.sliceString(from, to)
          let match

          while ((match = regex.exec(text))) {
            const variable = match[1]?.trim()
            if (
              variable &&
              (validVariables.includes(variable) ||
                validVariables.some(
                  (v) => variable.startsWith('!') && variable.match(v),
                ))
            ) {
              const deco = Decoration.mark({
                class: 'text-blue-600',
              })
              decorations.push(
                deco.range(
                  from + match.index,
                  from + match.index + match[0].length,
                ),
              )
            } else {
              const deco = Decoration.mark({
                class:
                  'underline-offset-2 decoration-wavy underline decoration-red-500 bg-red-50/50',
              })
              decorations.push(
                deco.range(
                  from + match.index,
                  from + match.index + match[0].length,
                ),
              )
            }
          }
        }

        return Decoration.set(decorations)
      }
    },
    { decorations: (v) => v.decorations },
  )

const buildApply =
  (keywords: PromptKeyword[]) =>
  (children: string[], kw: PromptKeyword): string => {
    const name = kw.apply
    const nestedVariables = keywords.filter(
      (v) => !v.isIterable && v.section.includes(name),
    )
    if (nestedVariables.length > 0) {
      nestedVariables.map((v) => {
        children.push(`{{${v.apply}}}`)
      })
    }
    const nestedIterables = keywords.filter(
      (v) => v.isIterable && v.section.includes(name),
    )
    if (nestedIterables.length > 0) {
      const builder = buildApply(keywords)
      nestedIterables.map((v) => {
        children.push(builder([], v))
      })
    }
    const apply =
      `{{#if ${name}}}\n` +
      `{{#each ${name}}}\n` +
      `${children.join('\n')}\n` +
      `{{/each}}\n` +
      `{{/if}}\n`

    return apply
  }

export const generateCompletions = (keywords: PromptKeyword[]) => {
  // Handle non-iterable variables
  const simpleResults = keywords
    .filter((v) => !v.isIterable && !v.key.includes('*'))
    .map((v) => ({
      apply: `{{${v.apply}}}`,
      type: 'Variable',
      section: v.section,
      label: v.key,
    }))

  const iterables = keywords.filter((v) => v.isIterable)
  const iterableResults: Completion[] = []

  iterables.forEach((iterable) => {
    const completion = {
      apply: buildApply(keywords)([], iterable),
      type: 'Iterable',
      section: iterable.section,
      label: iterable.key,
    }
    if (!iterableResults.some((v) => iterable.key.includes(v.label))) {
      iterableResults.push(completion)
    }
  })

  return [...simpleResults, ...iterableResults]
}

export function generateMarkdownDocs(items: Completion[]): string {
  let markdown = '# Documentation\n\n'
  markdown +=
    'This document outlines the available template variables and iterables, including their structure and usage patterns.\n\n'

  // TODO: explain how to use completion into the editor

  markdown += '**Usage Notes:**\n'
  markdown +=
    '- All iterables use Handlebars-style conditional and looping syntax\n'
  markdown +=
    '- Variables within `{{}}` can be directly embedded in templates\n'
  markdown += '- Nested structures require appropriate looping constructs\n'
  markdown +=
    '- All iterable sections are optional and protected by `{{#if}}` conditions\n'

  // Group items by section
  const sections: { [key: string]: Completion[] } = {}
  items.forEach((item) => {
    if (item.section) {
      if (!sections[item.section as string]) {
        sections[item.section as string] = []
      }
      sections[item.section as string]?.push(item)
    }
  })

  // Process each section
  for (const [section, sectionItems] of Object.entries(sections)) {
    const variables = sectionItems.filter((item) => item.type === 'Variable')
    const iterables = sectionItems.filter((item) => item.type === 'Iterable')

    // Section header
    const sectionTitle = section === '<generic>' ? 'Generic' : Cfl(section)
    if (variables.length > 0) {
      markdown += `## ${sectionTitle} Variables:\n`
      variables.forEach((item) => {
        markdown += '```handlebars\n'
        markdown += `${typeof item.apply === 'string' ? item.apply : ''}\n`
        markdown += '```\n'
      })
      markdown += `\n\n`
    }

    // Iterables
    if (iterables.length > 0) {
      markdown += `## ${sectionTitle} Iterables:\n`
      markdown += 'Complex data structures that can be iterated over:\n'

      iterables.forEach((item) => {
        if (!item.apply || typeof item.apply !== 'string') return
        markdown += `### ${toTitleCase(item.label.replace('*', ''))}:\n`
        markdown += `- **Type:** ${item.type}\n`
        markdown += '- **Template Usage:**\n'
        markdown += '```handlebars\n'
        markdown += `${item.apply.trim()}\n`
        markdown += '```\n'

        // Extract fields from the template
        const fields = extractFields(item.apply)
        if (fields.length > 0) {
          markdown += '- **Fields:**\n'
          fields.forEach((field) => {
            markdown += `  - ${field}\n`
          })
        }
        markdown += '\n'
      })
    }
  }

  return markdown
}

function toTitleCase(input: string): string {
  return input
    .split(/\.|(?=[A-Z])/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Helper function to extract fields from template strings
function extractFields(template: string): string[] {
  const fields: Set<string> = new Set()
  const fieldRegex = /{{([^#/][^{}]*)}}/g
  let match

  while ((match = fieldRegex.exec(template)) !== null) {
    const field = match[1]?.trim()
    if (field && !field.startsWith('if') && !field.startsWith('each')) {
      const f = field.split('.')[field.split('.').length - 1]
      if (f) fields.add(f)
    }
  }

  return Array.from(fields)
}
